import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, Form, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import phoneFieldNormalize from 'utils/normalize/phoneFieldNormalize'
import renderMaskedInput from 'components/FormControls/maskedInputField'
import phoneMask from 'utils/constants/phoneMask'
import renderSelect from 'components/FormControls/select'
import GridSpinner from 'components/UI/GridSpinner'
import SERVICES_API_V1 from 'api/services/v1'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/SubmitButtons'
import { isRequiredField } from 'utils/isRequiredField'

export const MiAdsSchema = () =>
  Yup.object().shape({
    network: Yup.string().required('common::validation_messages.required'),
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    firstname: Yup.string().required('common::validation_messages.required'),
    lastname: Yup.string().required('common::validation_messages.required'),
    legal_entity_name: Yup.string().required('common::validation_messages.required'),
    legal_entity_address: Yup.string().required('common::validation_messages.required'),
    ogrn: Yup.string().when(['country'], {
      is: (country) => country === 'ru',
      then: () => Yup.string().required('common::validation_messages.required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    industry: Yup.string().required('common::validation_messages.required'),
    country: Yup.string().required('common::validation_messages.required'),
    email: Yup.string()
      .test('emailValidator', 'common::validation_messages.incorrect_email_format', emailValidator)
      .required('common::validation_messages.required'),
    phone: Yup.string()
      .test(
        'phoneValidate',
        'common::validation_messages.incorrect_phone_format',
        function test(value) {
          const { path, createError } = this

          if (!value || value.length === 0) {
            return true
          }

          return (
            value.match(/^\+\d{7,15}$/) ||
            createError({
              path,
              message: 'common::validation_messages.incorrect_phone_format',
            })
          )
        },
      )
      .required('common::validation_messages.required'),
  })

let CreateMiAdsAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  formHeader,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
  networkType,
}) => {
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  const [settingsLoading, setSettingsLoading] = useState(true)
  const [countries, setCountries] = useState([])
  const [industriesLoading, setIndustriesLoading] = useState(true)
  const [industriesList, setIndustriesList] = useState([])

  const { form: { email, country } = {}, user } = useSelector((state) => ({
    form: selector(state, 'email', 'country'),
    user: state['app'].user,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  useEffect(() => {
    if (!networkId) {
      return
    }

    if (industriesList.length === 0) {
      SERVICES_API_V1.getNetworkSettings({ networkId })
        .then(({ response: { data } }) => {
          setCountries(
            (data?.countries || []).map(({ id, name }) => ({
              value: id,
              label: name,
            })),
          )

          setIndustriesList(
            (data?.industries || []).map((key) => ({
              value: key,
              label: t(`industries::${key}`),
            })),
          )
        })
        .finally(() => {
          setSettingsLoading(false)
          setIndustriesLoading(false)
        })
    } else {
      setIndustriesLoading(false)
    }
  }, [networkId])

  return (
    <>
      {formHeader}

      <Form
        onSubmit={handleSubmit(submitAccountData)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.mi-ads.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="firstname"
            required={isRequiredField(MiAdsSchema(networkType), 'firstname')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.mi-ads.fields.firstname.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="lastname"
            required={isRequiredField(MiAdsSchema(networkType), 'lastname')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.mi-ads.fields.lastname.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t('services::forms.register_account.mi-ads.fields.country.label')}
            name="country"
            required={isRequiredField(MiAdsSchema(networkType), 'country')}
            component={renderSelect}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
            readOnly={settingsLoading}
            fullWidth
            t={t}
            normalize={(value = '') => value}
            options={countries}
            mode={'filter'}
            noResultsLabel={t('common::empty_results.header')}
            ControlProps={{
              startAdornment: settingsLoading && <GridSpinner width={24} />,
            }}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="legal_entity_name"
            required={isRequiredField(MiAdsSchema(networkType), 'legal_entity_name')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.mi-ads.fields.legal_entity_name.label')}
            t={t}
          />

          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.mi-ads.fields.legal_entity_name.tooltip')}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="legal_entity_address"
            required={isRequiredField(MiAdsSchema(networkType), 'legal_entity_address')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.mi-ads.fields.legal_entity_address.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="ogrn"
            required={country === 'ru'}
            component={renderInputField}
            label={t('services::forms.register_account.mi-ads.fields.ogrn.label')}
            normalize={(value = '') => value.replace(/\D/g, '').substr(0, 13)}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t('services::forms.register_account.mi-ads.fields.industry.label')}
            name="industry"
            required={isRequiredField(MiAdsSchema(networkType), 'industry')}
            component={renderSelect}
            fullWidth
            t={t}
            normalize={(value) => value ?? ''}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
            options={industriesList}
            readOnly={industriesLoading}
            ControlProps={{
              startAdornment: industriesLoading && <GridSpinner width={24} />,
            }}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="email"
            required={isRequiredField(MiAdsSchema(networkType), 'email')}
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.mi-ads.fields.email.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            normalize={phoneFieldNormalize}
            name="phone"
            required={isRequiredField(MiAdsSchema(networkType), 'phone')}
            component={renderMaskedInput}
            placeholderChar={'\u2002'}
            placeholder={'+ ( )'}
            mask={phoneMask}
            label={t('services::forms.register_account.mi-ads.fields.phone.label')}
            t={t}
          />
        </div>

        <SubmitButtons
          formName={form}
          submitting={submitting}
          onClose={onClose}
        />
      </Form>
    </>
  )
}

CreateMiAdsAccountForm = reduxForm({
  form: 'CreateMiAdsAccountForm',
  initialValues: {
    network: '',
    login: '',
    firstname: '',
    lastname: '',
    legal_entity_name: '',
    legal_entity_address: '',
    industry: '',
    country: '',
    email: '',
    phone: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateMiAdsAccountForm)

CreateMiAdsAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
  networkType: PropTypes.string,
}

export default CreateMiAdsAccountForm
